import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import PONPISH_LOGO from '../../assets/images/common/common_header_ponpish_logo.svg';
import GOOGLE_STORE from '../../assets/images/common/common_header_google_store.svg';
import APPLE_STORE from '../../assets/images/common/common_header_apple_store.svg';
import COMMON_LEFT_APPLE from '../../assets/images/common/common_header_left_menu_apple.svg';
import COMMON_LEFT_GOOGLE from '../../assets/images/common/common_header_left_menu_google.svg';
import QRDownloadPopup from './QRDownloadPopup';
import { handleAppDownload } from '../../utils/mobileUtils';

const Header = () => {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const [isQRPopupOpen, setIsQRPopupOpen] = useState(false);

  const scrollToTop = () => {
    window.scrollTo(0, 0);
  };

  const openMobileMenu = () => {
    setIsMenuVisible(true);
    setTimeout(() => {
      setIsMobileMenuOpen(true);
    }, 10); // 약간의 딜레이로 트랜지션 트리거
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setIsAnimating(true);
    setTimeout(() => {
      setIsMenuVisible(false);
      setIsAnimating(false);
    }, 300); // 애니메이션 시간과 맞춤
  };

  const onAppDownloadClick = (e: React.MouseEvent) => {
    e.preventDefault();
    handleAppDownload(setIsQRPopupOpen);
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md h-[60px] md:h-[80px]">
        <div className="container mx-auto px-4 h-full">
          <div className="flex items-center justify-between h-full">
            <div className="flex items-center">
              <Link to="/" className="flex items-center" onClick={scrollToTop}>
                <img
                  src={PONPISH_LOGO}
                  alt="PONPISH"
                  className="h-[70px] md:h-[80px] w-auto"
                />
              </Link>
              <nav className="hidden md:flex items-center space-x-10 ml-8">
                <a
                  href="https://ponpish.co.kr/"
                  target="_blank"
                  rel="noopener"
                  className="text-lg font-medium transition-colors duration-200 text-black hover:text-[#7A2FC0]"
                >
                  쇼핑하기
                </a>
                <Link
                  to="/partner"
                  className={`text-lg font-medium transition-colors duration-200 ${location.pathname === '/partner'
                    ? 'text-blue-600'
                    : 'text-black hover:text-[#7A2FC0]'
                    }`}
                  onClick={scrollToTop}
                >
                  파트너안경원
                </Link>
                <Link
                  to="/brand"
                  className={`text-lg font-medium transition-colors duration-200 ${location.pathname === '/brand'
                    ? 'text-blue-600'
                    : 'text-black hover:text-[#7A2FC0]'
                    }`}
                  onClick={scrollToTop}
                >
                  제휴
                </Link>
              </nav>
            </div>
            <div className="hidden md:flex items-center space-x-[26px]">
              <a
                href="https://apps.apple.com/us/app/ponpish-%ED%8F%BC%ED%94%BC%EC%89%AC/id6743611917"
                onClick={onAppDownloadClick}
                className="px-5 py-2 rounded-full bg-ponpish-main text-white font-semibold text-base shadow-md hover:bg-ponpish-hover transition-colors duration-200 flex items-center cursor-pointer"
              >
                App 다운로드
              </a>
              <a
                href="https://apps.apple.com/us/app/ponpish-%ED%8F%BC%ED%94%BC%EC%89%AC/id6743611917"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:opacity-80 transition-opacity duration-200">
                <img
                  src={APPLE_STORE}
                  alt="Apple App Store"
                  className="h-8 w-auto"
                />
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.chaenggigo.ponpish"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:opacity-80 transition-opacity duration-200">
                <img
                  src={GOOGLE_STORE}
                  alt="Google Play Store"
                  className="h-8 w-auto"
                />
              </a>
            </div>
            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 text-black"
              onClick={openMobileMenu}
              aria-label="Toggle mobile menu"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {(isMenuVisible || isAnimating) && (
        <>
          {/* Dimmed background - 전체 화면 */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={closeMobileMenu}
          />
          {/* Side panel - 오른쪽에 붙도록 right-0 */}
          <div
            className={`fixed top-0 right-0 h-full w-[80vw] max-w-xs bg-white shadow-lg z-50
              transition-transform duration-300
              ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}
          >
            <div className="px-4 py-6 h-full flex flex-col justify-between">
              <div>
                {/* Close Button */}
                <button
                  className="absolute top-6 right-4 p-2 text-black"
                  onClick={closeMobileMenu}
                  aria-label="Close mobile menu"
                >
                  <svg
                    className="w-7 h-7"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
                {/* Mobile Navigation */}
                <nav className="flex flex-col space-y-1 mt-16">
                  <Link
                    to="/about"
                    className="font-pretendard font-normal text-[20px] text-black hover:text-[#7A2FC0] py-2 px-2"
                    onClick={() => {
                      closeMobileMenu();
                      scrollToTop();
                    }}>
                    About
                  </Link>
                  <a
                    href="https://ponpish.co.kr/"
                    target="_blank"
                    rel="noopener"
                    onClick={closeMobileMenu}
                    className="font-pretendard font-normal text-[20px] text-black hover:text-[#7A2FC0] py-2 px-2">
                    쇼핑하기
                  </a>
                  <Link
                    to="/partner"
                    className={`font-pretendard font-normal text-[20px] py-2 px-2 ${location.pathname === '/partner'
                      ? 'text-blue-600'
                      : 'text-black hover:text-[#7A2FC0]'
                      }`}
                    onClick={() => {
                      closeMobileMenu();
                      scrollToTop();
                    }}
                  >
                    파트너안경원
                  </Link>
                  <Link
                    to="/brand"
                    className={`font-pretendard font-normal text-[20px] py-2 px-2 ${location.pathname === '/brand'
                      ? 'text-blue-600'
                      : 'text-black hover:text-[#7A2FC0]'
                      }`}
                    onClick={() => {
                      closeMobileMenu();
                      scrollToTop();
                    }}>
                    제휴
                  </Link>
                </nav>
              </div>
              {/* Mobile App Download Section */}
              <div className="mb-4">
                {/* App Store / Google Play */}
                <div className="flex items-center justify-center space-x-2 mb-4">
                  <a
                    href="https://apps.apple.com/us/app/ponpish-%ED%8F%BC%ED%94%BC%EC%89%AC/id6743611917"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center pr-1 pl-4 py-4"
                    onClick={closeMobileMenu}
                  >
                    <img src={COMMON_LEFT_APPLE} alt="Apple App Store" className="h-5 w-auto" />
                    <span className="text-[14px] font-medium ml-3 text-black">App Store</span>
                  </a>
                  <span className="text-black mx-2">|</span>
                  <a
                    href="https://play.google.com/store/apps/details?id=com.chaenggigo.ponpish"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center pl-1 pr-4 py-4"
                    onClick={closeMobileMenu}
                  >
                    <img src={COMMON_LEFT_GOOGLE} alt="Google Play Store" className="h-5 w-auto" />
                    <span className="text-[14px] font-medium ml-3 text-black">Google Play</span>
                  </a>
                </div>
                {/* SNS Icons */}
                <div className="flex items-center justify-center space-x-4">
                  {/* 인스타그램 */}
                  <a href="https://www.instagram.com/ponpish_kr/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center rounded-full bg-black">
                    <svg fill="white" viewBox="0 0 24 24" width="22" height="22"><path d="M12 2.2c3.2 0 3.584.012 4.85.07 1.17.056 1.97.24 2.43.41.59.22 1.01.48 1.45.92.44.44.7.86.92 1.45.17.46.354 1.26.41 2.43.058 1.266.07 1.65.07 4.85s-.012 3.584-.07 4.85c-.056 1.17-.24 1.97-.41 2.43-.22.59-.48 1.01-.92 1.45-.44.44-.86.7-1.45.92-.46.17-1.26.354-2.43.41-1.266.058-1.65.07-4.85.07s-3.584-.012-4.85-.07c-1.17-.056-1.97-.24-2.43-.41-.59-.22-1.01-.48-1.45-.92-.44-.44-.7-.86-.92-1.45-.17-.46-.354-1.26-.41-2.43C2.212 15.784 2.2 15.4 2.2 12s.012-3.584.07-4.85c.056-1.17.24-1.97.41-2.43.22-.59.48-1.01.92-1.45.44-.44.86-.7 1.45-.92.46-.17 1.26-.354 2.43-.41C8.416 2.212 8.8 2.2 12 2.2zm0-2.2C8.736 0 8.332.012 7.052.07 5.77.128 4.8.312 4.05.55c-.77.25-1.42.58-2.07 1.23-.65.65-.98 1.3-1.23 2.07-.238.75-.422 1.72-.48 3C.012 8.332 0 8.736 0 12c0 3.264.012 3.668.07 4.948.058 1.28.242 2.25.48 3 .25.77.58 1.42 1.23 2.07.65.65 1.3.98 2.07 1.23.75.238 1.72.422 3 .48C8.332 23.988 8.736 24 12 24s3.668-.012 4.948-.07c1.28-.058 2.25-.242 3-.48.77-.25 1.42-.58 2.07-1.23.65-.65.98-1.3 1.23-2.07.238-.75.422-1.72.48-3 .058-1.28.07-1.684.07-4.948 0-3.264-.012-3.668-.07-4.948-.058-1.28-.242-2.25-.48-3-.25-.77-.58-1.42-1.23-2.07-.65-.65-1.3-.98-2.07-1.23-.75-.238-1.72-.422-3-.48C15.668.012 15.264 0 12 0z" /><path d="M12 5.838A6.162 6.162 0 1 0 12 18.162 6.162 6.162 0 1 0 12 5.838zm0 10.162A4 4 0 1 1 12 7.838a4 4 0 0 1 0 8.324zm6.406-11.845a1.44 1.44 0 1 0 0 2.88 1.44 1.44 0 0 0 0-2.88z" /></svg>
                  </a>
                  {/* 카카오톡 */}
                  <a href="https://pf.kakao.com/_Hycxin/chat" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center rounded-full bg-[#FEE500]">
                    <svg viewBox="0 0 24 24" width="22" height="22"><path fill="#3C1E1E" d="M12 3C6.48 3 2 6.48 2 10.5C2 12.81 3.94 14.77 6.5 15.97L5.5 18.5L8.5 16.5C9.5 16.75 10.5 16.9 11.5 16.9C17.02 16.9 21.5 13.42 21.5 9.4C21.5 5.38 17.02 3 12 3Z" /></svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* QR 다운로드 팝업 */}
      <QRDownloadPopup
        open={isQRPopupOpen}
        onClose={() => setIsQRPopupOpen(false)}
      />
    </>
  );
};

export default Header; 